import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  LightModeOutlined,
  DarkModeOutlined,
  AccountCircle,
  Logout,
  CloseRounded,
  MarkEmailReadRounded,
  ChevronLeftRounded,
  NotificationsNoneRounded,
} from "@mui/icons-material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import FlexBetween from "components/FlexBetween";
import { useDispatch } from "react-redux";
import { setMode } from "state";
import {
  Typography,
  AppBar,
  Card,
  IconButton,
  Stack,
  Toolbar,
  useTheme,
  useMediaQuery,
  Popover,
  Button,
  Divider,
  Backdrop,
  Avatar,
  Box,
  Badge,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { doSignOut } from "../firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useStateContext } from "contexts/ContextProvider";
//import { usePagePresence } from "hooks/useAssignmentPresence";
import { getAvatarColor, getAvatarInitials } from "helpers/avatarHelpers";
import { hasSeenLinguistOnboarding } from "helpers/linguistOnboarding";
import api from "services/api";
import linguistApi from "services/linguistApi";
import { NAVBAR_HEIGHT } from "constants/layout";
import { useWebSocket } from "contexts/WebSocketContext";

const NOTIFICATION_SOUND_STORAGE_KEY = "ce_portal.notificationSoundPreference";
const BROWSER_NOTIFICATION_ENABLED_STORAGE_KEY = "ce_portal.browserNotificationsEnabled";
const NOTIFICATION_SYNC_INTERVAL_MS = 10000;

const readBrowserNotificationPreference = () => {
  try {
    const cached = window.localStorage.getItem(BROWSER_NOTIFICATION_ENABLED_STORAGE_KEY);
    if (cached === null) return true;
    return cached !== "false";
  } catch (error) {
    return true;
  }
};

const getNotificationGroupKey = (notification) => {
  const candidate = notification?.VisibleAt || notification?.createdAt;
  if (!candidate) return "unknown";

  const parsed = new Date(candidate);
  if (Number.isNaN(parsed.getTime())) return String(candidate);

  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")} ${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`;
};

const parseReadFlag = (value) => {
  if (value === true || value === false) return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }
  return false;
};

const getNotificationCacheKey = (notification) => {
  if (!notification) return null;
  const numericId = Number(notification?.id);
  if (Number.isInteger(numericId) && numericId > 0) return `id:${numericId}`;

  const metadata = notification?.Metadata || {};
  const parts = [
    notification?.RecipientUserID,
    notification?.Type,
    notification?.SourceType,
    notification?.SourceID,
    metadata?.serviceId,
    metadata?.commentId,
    metadata?.routePath,
    notification?.Title,
    notification?.Message,
    notification?.createdAt || notification?.VisibleAt,
  ]
    .filter((item) => item !== undefined && item !== null && item !== "")
    .map(String);

  if (parts.length === 0) return null;
  return `fallback:${parts.join("|")}`;
};

const normalizeNotification = (notification) => ({
  ...notification,
  IsRead: parseReadFlag(notification?.IsRead),
  __notificationCacheKey: getNotificationCacheKey(notification),
});

// Detect whether a notification is intended for linguists only.
// Server may include explicit metadata flags; fall back to heuristics.
const isLinguistNotification = (notification) => {
  if (!notification) return false;
  const metadata = notification?.Metadata || {};

  if (metadata?.linguist === true || metadata?.for === "linguist" || metadata?.isLinguistNotification === true) {
    return true;
  }

  const type = String(notification?.Type || "").toLowerCase();
  if (
    type.includes("linguist") ||
    type.includes("assignment") ||
    type.includes("offer") ||
    type.includes("assignment_offer") ||
    // service comments should be delivered to linguists when relevant
    type.includes("service_comment") ||
    type.includes("service comment")
  ) {
    return true;
  }

  if (notification?.SourceType === "LinguistAssignmentOffer") return true;

  // Interpretations are often relevant to linguists (assignment offers, start reminders)
  if (notification?.SourceType === "Service" && String(metadata?.serviceCategory || "").toLowerCase() === "interpretation") {
    return true;
  }

  return false;
};

const getNotificationTimestamp = (notification) => {
  const candidate = notification?.VisibleAt || notification?.createdAt;
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const sortNotificationsLatestFirst = (items) =>
  [...items].sort((a, b) => {
    const readBucketDifference = Number(parseReadFlag(a?.IsRead)) - Number(parseReadFlag(b?.IsRead));
    if (readBucketDifference !== 0) {
      return readBucketDifference;
    }

    return getNotificationTimestamp(b) - getNotificationTimestamp(a);
  });

const NavbarLinguist = ({ user }) => {
  const dispatch = useDispatch();
  //const { viewers } = usePagePresence();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const isLinguistPortal = location.pathname.startsWith("/linguist-portal");
  const navigate = useNavigate();
  const { socket } = useWebSocket();
  const {
    setOverlayData,
    customPageTitle,
    activeMenu,
    setActiveMenu,
    isCollapsed: contextIsCollapsed,
    setIsCollapsed: setContextIsCollapsed,
    showSnackbar,
  } = useStateContext();

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const notificationPopoverOpen = Boolean(notificationAnchorEl);
  const [notifications, setNotifications] = useState([]);
  const [floatingNotifications, setFloatingNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const knownNotificationIdsRef = useRef(new Set());
  const knownNotificationReadStateRef = useRef(new Map());
  const hasInitializedNotificationSyncRef = useRef(false);
  const notificationsLastFetchedAtRef = useRef(0);
  const floatingNotificationTimersRef = useRef(new Map());
  const [notificationSound, setNotificationSound] = useState(() => {
    try {
      const cached = window.localStorage.getItem(NOTIFICATION_SOUND_STORAGE_KEY);
      return cached
        ? JSON.parse(cached)
        : {
            enabled: true,
            soundKey: "",
            soundUrl: "",
          };
    } catch (error) {
      return {
        enabled: true,
        soundKey: "",
        soundUrl: "",
      };
    }
  });
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(() =>
    readBrowserNotificationPreference()
  );

  const notificationsRoute = useMemo(
    () => (isLinguistPortal ? "/linguist-portal/notifications" : "/notifications"),
    [isLinguistPortal],
  );

  const isVisibleNow = (notification) => {
    if (!notification?.VisibleAt) return true;
    const visibleAtDate = new Date(notification.VisibleAt);
    if (Number.isNaN(visibleAtDate.getTime())) return true;
    return visibleAtDate.getTime() <= Date.now();
  };

  const handleNotificationsClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    requestBrowserNotificationPermission();

    const isStale = Date.now() - notificationsLastFetchedAtRef.current > 45000;
    const shouldRefresh = (notifications.length === 0 || isStale) && !loadingNotifications;

    if (shouldRefresh) {
      fetchNotifications({ silent: true });
    }
  };

  const handleNotificationsClose = () => {
    setNotificationAnchorEl(null);
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const playNotificationSound = useCallback(() => {
    if (!notificationSound.enabled || !notificationSound.soundUrl) return;

    try {
      const audio = new Audio(notificationSound.soundUrl);
      audio.volume = 1;
      audio.play().catch(() => {});
    } catch (error) {}
  }, [notificationSound.enabled, notificationSound.soundUrl]);

  const requestBrowserNotificationPermission = useCallback(() => {
    if (!hasSeenLinguistOnboarding()) return; // first visit: the tour owns the pretext + the request
    if (!browserNotificationsEnabled) return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "default") return;

    Notification.requestPermission().catch(() => {});
  }, [browserNotificationsEnabled]);

  const dismissFloatingNotification = useCallback((notificationId) => {
    const existingTimer = floatingNotificationTimersRef.current.get(notificationId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      floatingNotificationTimersRef.current.delete(notificationId);
    }

    setFloatingNotifications((prev) => prev.filter((item) => item.id !== notificationId));
  }, []);

  const dismissFloatingNotificationGroup = useCallback((groupItems) => {
    const ids = groupItems.map((item) => item.id);

    ids.forEach((notificationId) => {
      const existingTimer = floatingNotificationTimersRef.current.get(notificationId);
      if (existingTimer) {
        window.clearTimeout(existingTimer);
        floatingNotificationTimersRef.current.delete(notificationId);
      }
    });

    setFloatingNotifications((prev) =>
      prev.filter((item) => !ids.includes(item.id))
    );
  }, []);

  const showFloatingNotification = useCallback((notification) => {
    if (!notification?.id) return;

    setFloatingNotifications((prev) => {
      if (prev.some((item) => item.id === notification.id)) {
        return prev;
      }

      return [notification, ...prev].slice(0, 3);
    });

    const existingTimer = floatingNotificationTimersRef.current.get(notification.id);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    const timeoutId = window.setTimeout(() => {
      floatingNotificationTimersRef.current.delete(notification.id);
      setFloatingNotifications((prev) => prev.filter((item) => item.id !== notification.id));
    }, 30000);

    floatingNotificationTimersRef.current.set(notification.id, timeoutId);
  }, []);

  const fetchNotifications = useCallback(async ({ playSoundForNew = false, silent = false } = {}) => {
    if (!user?.id) return;
    if (!silent) {
      setLoadingNotifications(true);
    }

    try {
      const countResponse = await linguistApi.get("/notifications/count");
      const listResponse = await linguistApi.get("/notifications?limit=20");
      const nextNotifications = Array.isArray(listResponse.data)
        ? listResponse.data.map(normalizeNotification)
        : [];
      // Keep only linguist-relevant notifications to avoid duplicating member notifications
      const filtered = nextNotifications.filter(isLinguistNotification);
      const sortedNotifications = sortNotificationsLatestFirst(filtered);
      const knownIds = knownNotificationIdsRef.current;
      const knownReadStates = knownNotificationReadStateRef.current;

      if (!hasInitializedNotificationSyncRef.current) {
        sortedNotifications.forEach((item) => {
          knownIds.add(item.id);
          knownReadStates.set(item.id, parseReadFlag(item.IsRead));
        });
        hasInitializedNotificationSyncRef.current = true;
      } else {
        const newlyDiscoveredUnread = sortedNotifications.filter(
          (item) => {
            if (parseReadFlag(item.IsRead)) return false;

            const isNewId = !knownIds.has(item.id);
            const wasReadBefore = knownReadStates.get(item.id) === true;

            return isNewId || wasReadBefore;
          }
        );

        sortedNotifications.forEach((item) => {
          knownIds.add(item.id);
          knownReadStates.set(item.id, parseReadFlag(item.IsRead));
        });

        if (playSoundForNew) {
          const now = Date.now();
          const freshNotifications = newlyDiscoveredUnread.filter((item) => {
            const timestamp = getNotificationTimestamp(item);
            return timestamp > 0 && now >= timestamp && now - timestamp <= 3600000;
          });

          freshNotifications.forEach((item) => {
            showFloatingNotification(item);

            const isBackground =
              document.visibilityState !== "visible" || !document.hasFocus();

            if (
              browserNotificationsEnabled &&
              isBackground &&
              "Notification" in window &&
              Notification.permission === "granted"
            ) {
              try {
                const title = item?.Title || "Notification";
                const message = item?.Message || "";
                const desktopNotification = new Notification(title, {
                  body: message,
                  tag: `ce-notification-${item?.id || Date.now()}`,
                  renotify: true,
                });

                desktopNotification.onclick = () => {
                  window.focus();
                  navigate(notificationsRoute);
                  desktopNotification.close();
                };

                window.setTimeout(() => {
                  desktopNotification.close();
                }, 12000);
              } catch (error) {}
            }
          });

          if (freshNotifications.length > 0) {
            playNotificationSound();
          }
        }
      }

      setNotifications(sortedNotifications);
      // unread count from server is global for user; keep it but UI shows only linguist items
      setUnreadCount(Number(countResponse?.data?.unreadCount || 0));
      notificationsLastFetchedAtRef.current = Date.now();
    } catch (error) {
      setNotifications([]);
    } finally {
      if (!silent) {
        setLoadingNotifications(false);
      }
    }
  }, [
    user?.id,
    playNotificationSound,
    showFloatingNotification,
    navigate,
    browserNotificationsEnabled,
    notificationsRoute,
  ]);

  const fetchNotificationSoundPreference = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await linguistApi.get("/notificationSettings/linguist/preferences");
      const preference = response.data?.preference || null;
      const soundOptions = Array.isArray(response.data?.soundOptions) ? response.data.soundOptions : [];
      const selectedSound = soundOptions.find((option) => option.key === preference?.SoundPreset) || soundOptions[0] || null;

      const nextSoundState = {
        enabled: preference?.IsSoundEnabled !== false,
        soundKey: selectedSound?.key || "",
        soundUrl: selectedSound?.url || "",
      };

      setNotificationSound(nextSoundState);
      window.localStorage.setItem(NOTIFICATION_SOUND_STORAGE_KEY, JSON.stringify(nextSoundState));
    } catch (error) {}
  }, [user?.id]);

  const markNotificationAsRead = useCallback(async (notificationId) => {
    try {
      await linguistApi.patch(`/notifications/${notificationId}/read`);
      knownNotificationReadStateRef.current.set(notificationId, true);
      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => (item.id === notificationId ? { ...item, IsRead: true, ReadAt: new Date().toISOString() } : item))
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      fetchNotifications({ silent: true });
    }
  }, [fetchNotifications]);

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await linguistApi.patch("/notifications/read-all");
      notifications.forEach((item) => {
        knownNotificationReadStateRef.current.set(item.id, true);
      });
      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => ({ ...item, IsRead: true, ReadAt: item.ReadAt || new Date().toISOString() }))
        )
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      fetchNotifications({ silent: true });
    }
  }, [fetchNotifications, notifications]);

  const resolveNotificationRoute = useCallback(async (notification) => {
    const metadata = notification?.Metadata || {};

    if (typeof metadata?.routePath === "string" && metadata.routePath.startsWith("/")) {
      const isLegacyGenericServicePath = /^\/services\/\d+$/.test(metadata.routePath);
      if (!isLegacyGenericServicePath) {
        // Extract service ID from the route path to validate access
        const serviceIdMatch = metadata.routePath.match(/\/(?:interpretations|translations|otherServices)\/(\d+)/);
        let serviceId = null;
        
        if (serviceIdMatch) {
          serviceId = Number(serviceIdMatch[1]);
        } else {
          // Fallback: try to get service ID from metadata
          serviceId = metadata?.serviceId || metadata?.id;
        }
        
        // If we found a service ID, validate access
        if (serviceId) {
          try {
            // Validate linguist has access to this service
            const accessResponse = await linguistApi.get(`/services/${serviceId}/can-access`);
            if (!accessResponse?.data?.canAccess) {
              return null; // No access to this service
            }
            return metadata.routePath; // Access confirmed, return the path
          } catch (error) {
            return null; // Failed to verify access
          }
        }
        
        // Could not validate access - return null to prevent navigation
        return null;
      }
    }

    if (notification?.SourceType === "Service" && notification?.SourceID) {
      if (metadata?.serviceCategory === "Interpretation") return `/interpretations/${notification.SourceID}`;
      if (metadata?.serviceCategory === "Translation") return `/translations/${notification.SourceID}`;
      if (metadata?.serviceCategory === "Other") return `/otherServices/${notification.SourceID}`;

      try {
        // Check if linguist has access before trying to look up service route
        const accessResponse = await linguistApi.get(`/services/${notification.SourceID}/can-access`);
        if (!accessResponse?.data?.canAccess) {
          return null; // No access to this service
        }
        
        const routeResponse = await linguistApi.get(`/services/lookup/${notification.SourceID}`);
        const routePath = routeResponse?.data?.routePath;
        if (typeof routePath === "string" && routePath.startsWith("/")) {
          return routePath;
        }
      } catch (error) {
        return null;
      }

      return null;
    }

    if (notification?.SourceType === "ServiceNote" && metadata?.serviceId) {
      if (metadata?.serviceCategory === "Interpretation") return `/interpretations/${metadata.serviceId}`;
      if (metadata?.serviceCategory === "Translation") return `/translations/${metadata.serviceId}`;

      try {
        // Check if linguist has access before trying to look up service route
        const accessResponse = await linguistApi.get(`/services/${metadata.serviceId}/can-access`);
        if (!accessResponse?.data?.canAccess) {
          return null; // No access to this service
        }
        
        const routeResponse = await linguistApi.get(`/services/lookup/${metadata.serviceId}`);
        const routePath = routeResponse?.data?.routePath;
        if (typeof routePath === "string" && routePath.startsWith("/")) {
          return routePath;
        }
      } catch (error) {
        return null;
      }

      return `/otherServices/${metadata.serviceId}`;
    }

    if (notification?.SourceType === "Task") {
      const taskId = metadata?.taskId || notification?.SourceID;
      const metadataEntityType = metadata?.entityType;
      const metadataEntityId = metadata?.entityId;
      const metadataServiceType = String(metadata?.serviceType || "").toLowerCase();

      if (metadataEntityType === "Service" && metadataEntityId) {
        if (metadataServiceType.includes("interpretation")) return `/interpretations/${metadataEntityId}`;
        if (metadataServiceType.includes("translation")) return `/translations/${metadataEntityId}`;
        return `/otherServices/${metadataEntityId}`;
      }

      if (!taskId) return null;

      try {
        const response = await api.get(`/tasks/${taskId}`);
        const task = response?.data;

        if (task?.EntityType === "Service" && task?.EntityID) {
          const serviceType = String(task?.ServiceType || "").toLowerCase();
          if (serviceType.includes("interpretation")) return `/interpretations/${task.EntityID}`;
          if (serviceType.includes("translation")) return `/translations/${task.EntityID}`;
          return `/otherServices/${task.EntityID}`;
        }
      } catch (error) {
        return null;
      }
    }

    return null;
  }, []);

  const handleNotificationItemClick = useCallback(
    async (notification) => {
      try {
        const route = await resolveNotificationRoute(notification);
        if (route) {
          let markAsReadPromise = null;
          if (!parseReadFlag(notification?.IsRead)) {
            markAsReadPromise = markNotificationAsRead(notification.id).catch((error) => {
              console.error("Failed to mark notification as read after click:", error);
            });
          }

          handleNotificationsClose();
          navigate(route);

          // Keep this fire-and-forget so opening the route is never blocked.
          void markAsReadPromise;
        } else {
          // Route is null - likely due to access denied on service lookup
          showSnackbar("You do not have access to this assignment",theme, "warning");
        }
      } catch (error) {
        console.error("Failed to open notification route:", error);
        showSnackbar("Failed to open notification",theme, "error");
      }
    },
    [markNotificationAsRead, navigate, resolveNotificationRoute, showSnackbar, theme]
  );

  useEffect(() => {
    fetchNotifications({ playSoundForNew: true });
    fetchNotificationSoundPreference();
  }, [fetchNotifications, fetchNotificationSoundPreference]);


  useEffect(() => {
    if (!user?.id) return;

    const syncNotifications = () => {
      fetchNotifications({
        playSoundForNew: !notificationPopoverOpen,
        silent: true,
      });
    };

    const handleVisibilityOrFocus = () => {
      syncNotifications();
    };

    const intervalId = window.setInterval(() => {
      syncNotifications();
    }, NOTIFICATION_SYNC_INTERVAL_MS);

    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, [user?.id, notificationPopoverOpen, fetchNotifications]);

  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleNotificationCreated = ({ notification }) => {
      if (!notification) return;
      const normalizedNotification = normalizeNotification(notification);
      if (!isVisibleNow(normalizedNotification)) return;

      if (!isLinguistNotification(normalizedNotification)) return; // ignore member notifications

      const cacheKey = getNotificationCacheKey(normalizedNotification);
      if (cacheKey && knownNotificationIdsRef.current.has(cacheKey)) {
        return;
      }
      
      // Guard against retroactive loads (socket reconnection replays).
      // Use a 90-second window to account for server/client clock skew and
      // any processing delay between notification creation and socket delivery.
      const createdAt = new Date(normalizedNotification.createdAt);
      const now = new Date();
      const ageSeconds = (now.getTime() - createdAt.getTime()) / 1000;
      const isRecentlyCreated = Number.isNaN(ageSeconds) || ageSeconds < 120;

      setNotifications((prev) => {
        const alreadyExists = cacheKey && prev.some((item) => getNotificationCacheKey(item) === cacheKey);
        if (alreadyExists) return prev;
        return sortNotificationsLatestFirst([normalizedNotification, ...prev]).slice(0, 50);
      });
      
      if (isRecentlyCreated) {
        // Mark as known so the polling interval doesn't double-show it.
        if (cacheKey) {
          knownNotificationIdsRef.current.add(cacheKey);
          knownNotificationReadStateRef.current.set(cacheKey, false);
        }
        showFloatingNotification(normalizedNotification);

        const isBackground =
          document.visibilityState !== "visible" || !document.hasFocus();

        if (
          browserNotificationsEnabled &&
          isBackground &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          try {
            const title = normalizedNotification?.Title || "Notification";
            const message = normalizedNotification?.Message || "";
            const desktopNotification = new Notification(title, {
              body: message,
              tag: `ce-notification-${normalizedNotification?.id || Date.now()}`,
              renotify: true,
            });

            desktopNotification.onclick = () => {
              window.focus();
              navigate(notificationsRoute);
              desktopNotification.close();
            };

            window.setTimeout(() => {
              desktopNotification.close();
            }, 12000);
          } catch (error) {}
        }

        playNotificationSound();
      }
      // If the notification is too old to show a card, do NOT add to knownIds.
      // The polling interval will then discover it as "new" and surface it with
      // a card (as long as it falls within the polling freshness window).
    };

    const handleNotificationCount = ({ userId, unreadCount: newUnreadCount }) => {
      if (Number(userId) !== Number(user.id)) return;
      setUnreadCount(Number(newUnreadCount || 0));
    };

    const handleNotificationUpdated = ({ notification }) => {
      if (!notification) return;
      const normalizedNotification = normalizeNotification(notification);
      if (!isLinguistNotification(normalizedNotification)) return; // ignore member updates
      const cacheKey = getNotificationCacheKey(normalizedNotification);
      if (!cacheKey) return;

      if (parseReadFlag(normalizedNotification.IsRead)) {
        knownNotificationReadStateRef.current.set(cacheKey, true);
      }

      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) =>
            getNotificationCacheKey(item) === cacheKey
              ? {
                  ...item,
                  ...normalizedNotification,
                  IsRead: parseReadFlag(normalizedNotification.IsRead),
                }
              : item
          )
        )
      );
    };

    const handleNotificationReadAll = ({ userId: eventUserId, readAt }) => {
      if (Number(eventUserId) !== Number(user.id)) return;

      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => {
            knownNotificationReadStateRef.current.set(item.id, true);
            return {
              ...item,
              IsRead: true,
              ReadAt: item.ReadAt || readAt || new Date().toISOString(),
            };
          })
        )
      );
      setUnreadCount(0);
    };

    const handleNotificationSoundPreferenceUpdated = (event) => {
      if (!event?.detail) return;
      setNotificationSound(event.detail);
    };

    const handleBrowserNotificationPreferenceUpdated = (event) => {
      if (typeof event?.detail?.enabled === "boolean") {
        setBrowserNotificationsEnabled(event.detail.enabled);
        return;
      }

      setBrowserNotificationsEnabled(readBrowserNotificationPreference());
    };

    const handleSocketConnect = () => {
      fetchNotifications({
        playSoundForNew: false,
        silent: true,
      });
    };

    socket.on("notification:created", handleNotificationCreated);
    socket.on("notification:updated", handleNotificationUpdated);
    socket.on("notification:read-all", handleNotificationReadAll);
    socket.on("notification:count", handleNotificationCount);
    socket.on("connect", handleSocketConnect);
    window.addEventListener("notification-sound-preference-updated", handleNotificationSoundPreferenceUpdated);
    window.addEventListener("browser-notification-preference-updated", handleBrowserNotificationPreferenceUpdated);

    return () => {
      socket.off("notification:created", handleNotificationCreated);
      socket.off("notification:updated", handleNotificationUpdated);
      socket.off("notification:read-all", handleNotificationReadAll);
      socket.off("notification:count", handleNotificationCount);
      socket.off("connect", handleSocketConnect);
      window.removeEventListener("notification-sound-preference-updated", handleNotificationSoundPreferenceUpdated);
      window.removeEventListener("browser-notification-preference-updated", handleBrowserNotificationPreferenceUpdated);
    };
  }, [
    socket,
    user?.id,
    playNotificationSound,
    showFloatingNotification,
    fetchNotifications,
    navigate,
    isLinguistPortal,
    browserNotificationsEnabled,
    notificationsRoute,
  ]);

  useEffect(() => {
    const timers = floatingNotificationTimersRef.current;

    return () => {
      timers.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      timers.clear();
    };
  }, []);

  const groupedFloatingNotifications = useMemo(() => {
    const grouped = new Map();

    floatingNotifications.forEach((item) => {
      const key = getNotificationGroupKey(item);
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }

      grouped.get(key).push(item);
    });

    return Array.from(grouped.entries()).map(([groupKey, items]) => ({
      groupKey,
      items: sortNotificationsLatestFirst(items),
    }));
  }, [floatingNotifications]);

  // State to handle the sign out process overlay
  const [signingOut, setSigningOut] = useState(false);

  // Determine if we should show the back button
  const showBackButton = useMemo(() => {
    const path = location.pathname;
    // Show back button on detail/profile pages
    return (
      path.startsWith("/linguist-portal/assignments/") ||
      path.startsWith("/linguist-portal/profile") ||
      path.startsWith("/linguist-portal/notifications") ||
      path.startsWith("/interpretations/") ||
      path.startsWith("/translations/") ||
      path.startsWith("/otherServices/") ||
      path.startsWith("/clients/") ||
      path.startsWith("/linguists/") ||
      path.startsWith("/invoices/") ||
      path.startsWith("/members/")
    );
  }, [location.pathname]);

  // Get the back destination based on current path
  const getBackDestination = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith("/linguist-portal/assignments/")) return "/linguist-portal/interpretations";
    if (path.startsWith("/linguist-portal/profile")) return "/linguist-portal";
    if (path.startsWith("/linguist-portal/notifications")) return "/linguist-portal";
    if (path.startsWith("/interpretations/")) return "/interpretations";
    if (path.startsWith("/translations/")) return "/translations";
    if (path.startsWith("/otherServices/")) return "/otherServices";
    if (path.startsWith("/clients/")) return "/clients";
    if (path.startsWith("/linguists/")) return "/linguists";
    if (path.startsWith("/invoices/")) return "/invoices";
    if (path.startsWith("/members/")) return "/members";
    return "/";
  }, [location.pathname]);

  const handleBackClick = () => {
    // Clear overlay state first
    setOverlayData(null);

    // If page was opened directly → no history inside the app
    if (location.key === "default") {
      navigate(getBackDestination);
    } else {
      navigate(-1);
    }
  };

  // Get page title based on current route
  const pageTitle = useMemo(() => {
    // If a custom page title is set (e.g., from LinguistProfile), use it
    if (customPageTitle) {
      return customPageTitle;
    }

    const path = location.pathname;

    // Handle root paths
    if (path === "/" || path === "/login" || path === "/loginConfirmation") {
      return "";
    }

    // Linguist portal routes
    if (path === "/linguist-portal") return "Dashboard";
    if (path === "/linguist-portal/interpretations") return "Interpretations";
    if (path === "/linguist-portal/notifications") return "Notifications";
    if (path.startsWith("/linguist-portal/assignments/")) return "Interpretation Details";
    if (path === "/linguist-portal/profile") return "Profile Settings";
    if (path.startsWith("/linguist-portal/setup-password")) return "Setup Password";

    // Handle specific routes
    if (path === "/dashboard") return "Dashboard";
    if (path === "/interpretations") return "Interpretations";
    if (path.startsWith("/interpretations/")) return "Interpretation Details";
    if (path === "/translations") return "Translations";
    if (path.startsWith("/translations/")) return "Translation Details";
    if (path === "/otherServices") return "Other Services";
    if (path.startsWith("/otherServices/")) return "Other Service Details";
    if (path === "/clients") return "Clients";
    if (path.startsWith("/clients/")) return "Client Profile";
    if (path === "/linguists") return "Linguists";
    if (path.startsWith("/linguists/")) return "Linguist Profile";
    if (path === "/calendar") return "Calendar";
    if (path === "/tasks") return "Tasks";
    if (path === "/help") return "Help";
    if (path === "/expenses") return "Expenses";
    if (path === "/invoices") return "Invoices";
    if (path.startsWith("/invoices/")) return "Invoice Details";
    if (path === "/invoicetemplate") return "Invoice Template";
    if (path === "/linguistsPayments") return "Linguists Payments";
    if (path === "/reports") return "Reports";
    if (path === "/members") return "Members";
    if (path.startsWith("/members/")) return "Member Profile";
    if (path === "/settings") return "Settings";
    if (path === "/404") return "Page Not Found";

    // Default fallback
    return "";
  }, [location.pathname, customPageTitle]);

  // Track whether the page title is visually truncated so the long-press/hover
  // reveal tooltip only appears when text is actually hidden. Re-measured on
  // resize and on layout width changes (e.g., sidebar collapse).
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const pageTitleRef = useRef(null);

  useEffect(() => {
    const el = pageTitleRef.current;
    if (!el) return;

    const measureTitleTruncation = () => {
      setIsTitleTruncated(el.scrollWidth > el.clientWidth);
    };

    measureTitleTruncation();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measureTitleTruncation);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measureTitleTruncation);
    return () => window.removeEventListener("resize", measureTitleTruncation);
  }, [pageTitle]);

  // Updated handleLogout to gradually change the overlay color to theme.palette.background.default:
  // 1. Set signingOut state true to trigger backdrop transition.
  // 2. Wait for 3 seconds for the backdrop to transition.
  // 3. Wait an additional 2 seconds.
  // 4. Call doSignOut and then remove the overlay.
  const handleLogout = async () => {
    setSigningOut(true);
    // Wait 3 seconds for the backdrop to transition to theme.palette.background.default

    handleClose();

    await new Promise((resolve) => setTimeout(resolve, 800));
    // Wait an additional 2 seconds while at the target overlay color
    await doSignOut();
    setSigningOut(false);
  };

  // Handle sidebar toggle
  const handleToggleSidebar = () => {
    if (isMobileScreen) {
      if (!activeMenu) {
        setActiveMenu(true);
        setContextIsCollapsed(false);
      } else {
        setActiveMenu(false);
      }
      return;
    }

    if (!activeMenu) {
      // If sidebar is closed, open it fully
      setActiveMenu(true);
      setContextIsCollapsed(false);
    } else if (!contextIsCollapsed) {
      // If sidebar is fully open, collapse it
      setContextIsCollapsed(true);
    } else if (contextIsCollapsed) {
      // If sidebar is collapsed, expand it (not close it)
      setContextIsCollapsed(false);
    }
  };

  return (
    <>
      <AppBar
        sx={{
          position: "static",
          background: "none",
          boxShadow: (theme) => theme.shadows[6],
          height: "100%",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "100%",
            height: "100%",
            py: 0,
            px: { xs: 1, sm: 2 },
            gap: 1,
          }}
        >
          {/* LEFT SIDE - Back Button and Page Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden", flex: { xs: 1, md: 1 } }}>
            {/* Mobile detail pages show Back INSTEAD of the hamburger (2026-10-05, Amine):
                one control instead of two. Desktop and list pages keep the hamburger. */}
            {!activeMenu && !(isMobileScreen && showBackButton) && (
              <IconButton
                onClick={handleToggleSidebar}
                sx={{
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <MenuRoundedIcon sx={{ fontSize: 32 }} />
              </IconButton>
            )}
            {showBackButton && (
              <IconButton
                onClick={handleBackClick}
                sx={{
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ChevronLeftRounded sx={{ fontSize: 32 }} />
              </IconButton>
            )}
            {pageTitle && (
              <Tooltip
                title={isTitleTruncated ? pageTitle : ""}
                enterTouchDelay={500}
                leaveTouchDelay={2000}
              >
                <Typography
                  ref={pageTitleRef}
                  variant="h2"
                  fontWeight="bold"
                  color={theme.palette.primary.main}
                  aria-label={pageTitle}
                  sx={{
                    display: "block",
                    flex: 1,
                    minWidth: 0,
                    pr: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: { xs: "1rem", sm: theme.typography.h2.fontSize },
                    lineHeight: { xs: 1.2, sm: theme.typography.h2.lineHeight },
                  }}
                >
                  {pageTitle}
                </Typography>
              </Tooltip>
            )}
            {/**
            <Box sx={{ display: { xs: "none", sm: "flex" } }}>
              <PresenceIndicator viewers={viewers} />
            </Box> */}
          </Box>

          {/* RIGHT SIDE - Desktop */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, ml: "auto" }}>
            <IconButton
              data-tour-id="nav-alerts"
              onClick={handleNotificationsClick}
              sx={{
                color: theme.palette.primary.main,
                borderRadius: "12px",
                bgcolor: theme.palette.background.default,
                boxShadow: theme.shadows[3],
                p: "8px",
                "&:hover": {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                max={99}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  "& .MuiBadge-badge": {
                    height: "20px",
                    minWidth: "20px",
                    borderRadius: "8px",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    px: 0.75,
                    top: -4,
                    right: -4,
                  },
                }}
              >
                <NotificationsNoneRounded />
              </Badge>
            </IconButton>

            <Popover
              open={notificationPopoverOpen}
              anchorEl={notificationAnchorEl}
              onClose={handleNotificationsClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    width: { xs: "calc(100vw - 32px)", sm: 360 },
                    maxWidth: "calc(100vw - 32px)",
                    maxHeight: "55vh",
                    borderRadius: 2,
                    overflow: "hidden",
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 0.5 }}>
                <Typography variant="h5" fontWeight="bold">Notifications</Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Button size="small" onClick={markAllNotificationsAsRead} disabled={!unreadCount}>
                    Mark all read
                  </Button>
                  <Button size="small" onClick={() => { handleNotificationsClose(); navigate(notificationsRoute); }}>
                    Show all
                  </Button>
                </Box>
              </Box>
              <Divider />

              {loadingNotifications ? (
                <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ py: 5, px: 2 }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    No notifications yet.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0, maxHeight: "calc(55vh - 60px)", overflowY: "auto" }}>
                  {notifications.map((item) => (
                    <ListItemButton
                      key={item.id}
                      onClick={() => handleNotificationItemClick(item)}
                      sx={{
                        alignItems: "flex-start",
                        bgcolor: parseReadFlag(item.IsRead) ? "transparent" : theme.palette.action.hover,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", gap: 1 }}>
                        <ListItemText
                          primary={item.Title || "Notification"}
                          secondary={
                            <Box component="div" sx={{ display: "block", mt: 0.5 }}>
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: "block" }}>
                                {item.Message || ""}
                              </Typography>
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                {formatRelativeTime(item.VisibleAt || item.createdAt)}
                              </Typography>
                            </Box>
                          }
                          primaryTypographyProps={{
                            fontWeight: parseReadFlag(item.IsRead) ? 500 : 700,
                          }}
                          secondaryTypographyProps={{
                            component: "div",
                          }}
                        />

                        {!parseReadFlag(item.IsRead) && (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              markNotificationAsRead(item.id);
                            }}
                            sx={{
                              mt: 0.25,
                              color: theme.palette.primary.main,
                              border: `1px solid ${theme.palette.primary.border2}`,
                              borderRadius: "10px",
                            }}
                            title="Mark as read"
                            aria-label="Mark as read"
                          >
                            <MarkEmailReadRounded fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Popover>

            {floatingNotifications.length > 0 && (
              <Box
                sx={{
                  position: "fixed",
                  top: `${NAVBAR_HEIGHT + 16}px`,
                  right: 16,
                  width: { xs: "calc(100vw - 32px)", sm: 360 },
                  maxWidth: "calc(100vw - 32px)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  zIndex: (theme) => theme.zIndex.snackbar,
                  pointerEvents: "none",
                }}
              >
                {groupedFloatingNotifications.map(({ groupKey, items }) => {
                  const firstItem = items[0];

                  return (
                    <Card
                      key={groupKey}
                      sx={{
                        pointerEvents: "auto",
                        borderRadius: "16px",
                        border: `1px solid ${theme.palette.primary.border2}`,
                        boxShadow: theme.shadows[8],
                        backgroundColor: theme.palette.background.paper,
                        cursor: "pointer",
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ p: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, justifyContent: "space-between" }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" sx={{ display: "block", color: theme.palette.primary.main, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
                              {items.length > 1
                                ? `${items.length} notifications`
                                : firstItem?.Type === "INTERPRETATION_START_REMINDER"
                                ? "Interpretation reminder"
                                : firstItem?.Type === "SERVICE_DEADLINE_REMINDER" || firstItem?.Type === "SERVICE_DEADLINE_TODAY" || firstItem?.Type === "SERVICE_DEADLINE_PASSED"
                                ? "Deadline reminder"
                                : firstItem?.Title || "Notification"}
                            </Typography>

                            <Stack spacing={0.75}>
                              {items.map((item) => {
                                const serviceName = item?.Metadata?.serviceName || item?.Title || "Notification";
                                const notificationSummary = item?.Metadata?.notificationSummary || item?.Message || "";

                                return (
                                  <Box
                                    key={item.id}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleNotificationItemClick(item);
                                      dismissFloatingNotification(item.id);
                                    }}
                                    sx={{
                                      borderRadius: "10px",
                                      px: 0.5,
                                      py: 0.5,
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor: theme.palette.action.hover,
                                      },
                                    }}
                                  >
                                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: "break-word" }}>
                                      {serviceName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ display: "block", wordBreak: "break-word" }}>
                                      {notificationSummary}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Stack>

                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              {formatRelativeTime(firstItem?.VisibleAt || firstItem?.createdAt)}
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              dismissFloatingNotificationGroup(items);
                            }}
                            sx={{ mt: -0.25, mr: -0.25, color: theme.palette.text.secondary }}
                          >
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            )}

          </Box>

          {/* User Profile */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: { xs: 0, md: 1.75 },
            }}
          >
              <Box sx={{ position: "relative", display: "inline-flex" }}>
              <IconButton
                data-tour-id="nav-avatar"
                onClick={handleClick}
                aria-label="Account menu"
                sx={{ p: 0 }}
              >
                <Avatar
                  sx={{
                    bgcolor: getAvatarColor(user?.Name),
                    width: { xs: 34, sm: 40 },
                    height: { xs: 34, sm: 40 },
                    borderRadius: "12px",
                    boxShadow: theme.shadows[3],
                  }}
                  src={user?.ImageThumbnail || user?.Image || undefined}
                >
                  {getAvatarInitials(user?.Name)}
                </Avatar>
              </IconButton>
              </Box>
              <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      minWidth: 260,
                      borderRadius: 2,
                      p: 1.5,
                    },
                  },
                }}
              >
                {/* Mobile actions — mirrors the 2026-09-30 admin Navbar
                    merge: the mobile 3-dot trigger was removed and
                    Notifications lives here instead. The notification
                    popover re-anchors to the avatar trigger (never
                    document.body — mobile popover anchor rule). */}
                {isMobileScreen && (
                  <>
                    <Button
                      startIcon={<NotificationsNoneRounded />}
                      fullWidth
                      sx={(theme) => ({
                        justifyContent: "flex-start",
                        mb: 1,
                        px: 2,
                        color: theme.palette.text.primary,
                        "&:hover": {
                          backgroundColor: theme.palette.primary[100],
                        },
                      })}
                      onClick={() => {
                        handleClose();
                        handleNotificationsClick({ currentTarget: anchorEl });
                      }}
                    >
                      Notifications {unreadCount > 0 ? `(${unreadCount})` : ""}
                    </Button>

                    <Divider sx={{ my: 1 }} />
                  </>
                )}

                <FlexBetween alignItems="center" gap={1.25} sx={{ mb: 1.5 }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <Avatar
                      sx={{
                        bgcolor: getAvatarColor(user?.Name),
                        width: 36,
                        height: 36,
                        borderRadius: "12px",
                        boxShadow: theme.shadows[3],
                      }}
                      src={user?.ImageThumbnail || user?.Image || undefined}
                    >
                      {getAvatarInitials(user?.Name)}
                    </Avatar>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight="bold">
                      {user?.Name || "User"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.Email || user?.email || ""}
                    </Typography>
                  </Box>
                </FlexBetween>

                <Divider sx={{ my: 1 }} />

                <Button
                  startIcon={<AccountCircle />}
                  fullWidth
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    mb: 1,
                    px: 2,
                    color: theme.palette.text.primary,
                    "&:hover": {
                      backgroundColor: theme.palette.primary[100],
                    },
                  })}
                  onClick={() => {
                    handleClose();
                    navigate("/linguist-portal/profile");
                  }}
                >
                  Profile
                </Button>

                <Button
                  startIcon={
                    theme.palette.mode === "dark" ? (
                      <LightModeOutlined />
                    ) : (
                      <DarkModeOutlined />
                    )
                  }
                  fullWidth
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    mb: 1,
                    px: 2,
                    color: theme.palette.text.primary,
                    "&:hover": {
                      backgroundColor: theme.palette.primary[100],
                    },
                  })}
                  onClick={() => {
                    dispatch(setMode());
                    handleClose();
                  }}
                >
                  Switch Theme
                </Button>

                <Divider sx={{ my: 1 }} />

                <Button
                  startIcon={<Logout />}
                  color="error"
                  fullWidth
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    px: 2,
                    color: theme.palette.error.main,
                    "&:hover": {
                      backgroundColor: theme.palette.error.light,
                      color: theme.palette.background.default,
                    },
                  })}
                  onClick={handleLogout}
                >
                  Log Out
                </Button>
              </Popover>
            </Box>

        </Toolbar>
      </AppBar>

      {/* Backdrop overlay during sign out with gradual transition to theme.palette.background.default */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.default,
        }}
        open={signingOut}
        transitionDuration={800} // Duration in milliseconds (3 seconds)
      ></Backdrop>
    </>
  );
};

export default NavbarLinguist;
